import React, { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import InputBase from '@mui/material/InputBase';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Search from '@mui/icons-material/Search';
import type { Partner } from '../../services/authService';

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter((word) => /^[A-Za-z0-9]/.test(word))
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

const PartnerMark: React.FC<{ name: string; size: number }> = ({ name, size }) => (
  <Box
    aria-hidden
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: size >= 40 ? 2.5 : 2,
      display: 'grid',
      placeItems: 'center',
      fontSize: size >= 40 ? 13 : 11,
      fontWeight: 600,
      color: 'text.secondary',
      bgcolor: 'action.hover',
    }}
  >
    {initials(name)}
  </Box>
);

interface PartnerPickerProps {
  partners: Partner[];
  selectedId: string;
  onChange: (partner: Partner) => void;
}

/** Searchable partner dropdown. */
const PartnerPicker: React.FC<PartnerPickerProps> = ({ partners, selectedId, onChange }) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const sorted = useMemo(() => [...partners].sort((a, b) => a.partnerName.localeCompare(b.partnerName)), [partners]);
  const needle = query.trim().toLowerCase();
  const matches = needle ? sorted.filter((p) => p.partnerName.toLowerCase().includes(needle) || p.partnerId.toLowerCase().includes(needle)) : sorted;
  const selected = partners.find((p) => p.partnerId === selectedId);

  const close = () => setOpen(false);
  const pick = (partner: Partner) => {
    close();
    if (partner.partnerId !== selectedId) onChange(partner);
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <ButtonBase
        ref={anchorRef}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Partner: ${selected?.partnerName ?? 'none selected'}`}
        onClick={() => {
          setQuery('');
          setOpen(true);
        }}
        sx={(theme) => ({
          width: '100%',
          minHeight: 56,
          py: 1,
          pl: 1,
          pr: 2,
          gap: 1.5,
          justifyContent: 'flex-start',
          textAlign: 'left',
          borderRadius: 3,
          border: 1,
          borderColor: open ? 'primary.dark' : 'divider',
          // Inset ring reads as a 2px border without shifting the content.
          boxShadow: open ? `inset 0 0 0 1px ${theme.palette.primary.dark}` : 'none',
          bgcolor: 'background.paper',
          '&:hover': { borderColor: open ? 'primary.dark' : 'text.secondary' },
          '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
        })}
      >
        <PartnerMark name={selected?.partnerName ?? ''} size={40} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography noWrap sx={{ fontSize: 15, fontWeight: 500 }}>
            {selected?.partnerName ?? 'Select a partner'}
          </Typography>
          {selected && (
            <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>
              {selected.urlSlugName || selected.partnerId}
            </Typography>
          )}
        </Box>
        {open ? <ExpandLess sx={{ color: 'text.secondary' }} /> : <ExpandMore sx={{ color: 'text.secondary' }} />}
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: anchorRef.current?.offsetWidth,
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              boxShadow: '0 8px 28px rgba(0,21,41,.18)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, height: 48, borderBottom: 1, borderColor: 'divider' }}>
          <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
          <InputBase
            autoFocus
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search partners"
            inputProps={{ 'aria-label': 'Search partners' }}
            sx={{ fontSize: 14 }}
          />
        </Box>
        <Box role="listbox" aria-label="Partners" sx={{ maxHeight: 288, overflowY: 'auto', py: 0.75 }}>
          {matches.map((partner) => {
            const isSelected = partner.partnerId === selectedId;
            return (
              <ButtonBase
                key={partner.partnerId}
                role="option"
                aria-selected={isSelected}
                onClick={() => pick(partner)}
                sx={(theme) => ({
                  width: '100%',
                  height: 48,
                  px: 2,
                  gap: 1.5,
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  fontSize: 14,
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  '&:hover, &.Mui-focusVisible': { bgcolor: 'action.hover' },
                })}
              >
                <PartnerMark name={partner.partnerName} size={28} />
                <Typography noWrap sx={{ flex: 1, fontSize: 14 }}>
                  {partner.partnerName}
                </Typography>
                {isSelected && <Check sx={{ fontSize: 20, color: 'primary.dark' }} />}
              </ButtonBase>
            );
          })}
          {matches.length === 0 && (
            <Typography sx={{ p: 2, fontSize: 13, color: 'text.secondary' }}>
              {partners.length ? `No partners match “${query}”` : 'No partners available for this account'}
            </Typography>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default PartnerPicker;
